import React from 'react'
import { History } from 'history'
import { Link as RouterLink } from 'react-router-dom'

import { IconError, IconInfo, IconWarning } from 'components/ui/icons'
import { space } from 'helpers/style'
import styled from 'styled'

const Container = styled.section`
  padding: ${space(4)};
  background-color: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.grayL70};
  border-radius: 4px;
`

const Message = styled.aside`
  display: flex;
  margin-bottom: ${space(3)};

  &:last-of-type {
    margin-bottom: 0;
  }
`

const Link = styled(RouterLink)`
  display: flex;
`

const MessageText = styled.span`
  margin-left: ${space(1)};
  color: ${({ theme }) => theme.color.darkText};
  font-size: ${({ theme }) => theme.font.size.small};
  font-weight: ${({ theme }) => theme.font.weight.light};
`

const MessageDescription = styled.span`
  margin-left: ${space(2)};
  color: ${({ theme }) => theme.color.grayL20};
  font-size: ${({ theme }) => theme.font.size.small};
  font-weight: ${({ theme }) => theme.font.weight.light};
`

type MessageDetails = {
  readonly text: string;
  readonly description?: string;
  readonly link?: History.LocationDescriptor<any>;
}

type MessageType = 'info' | 'warning' | 'error';

type Props = {
  readonly error?: ReadonlyArray<MessageDetails>;
  readonly info?: ReadonlyArray<MessageDetails>;
  readonly warning?: ReadonlyArray<MessageDetails>;
}

/**
 * This is used near the top of a page layout to display the current status of the entity or list of entities that are being viewed.
 *
 * Messages are grouped into three types, and they display in this order: (need to confirm this with Renny).
 *
 * - Informative messages
 * - Publishing warnings
 * - Validation errors
 */
export const StatusSummary = (props: Props) => {
  const { info, warning, error } = props

  const renderIcon = (type: MessageType) => {
    switch (type) {
      case 'info': {
        return <IconInfo size="base" color="info" />
      }

      case 'error': {
        return <IconError size="base" color="error" />
      }

      case 'warning': {
        return <IconWarning size="base" color="warning" />
      }
    }
  }

  const renderMessage = (type: MessageType, message: MessageDetails, index: number) => {
    const messageText = (
      <>
        {renderIcon(type)}
        <MessageText>{message.text}</MessageText>
        {message.description && (<MessageDescription>{message.description}</MessageDescription>)}
      </>
    )

    return message.link ? (
      <Message key={`${type}-${index}`} role="alert">
        <Link to={message.link} target="_blank">{messageText}</Link>
      </Message>
    ) : (
      <Message key={`${type}-${index}`} role="alert">
        {messageText}
      </Message>
    )
  }

  if (!info && !error && !warning) {
    return null
  }

  return (
    <Container>
      {info && info.map((message, index) => renderMessage('info', message, index))}
      {warning && warning.map((message, index) => renderMessage('warning', message, index))}
      {error && error.map((message, index) => renderMessage('error', message, index))}
    </Container>
  )
}
